import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { InfoIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Form, FormField, FormItem, FormLabel, FormMessage, FormControl } from '@/components/ui/form';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { BankAccount } from '@/types/pensions';

const bankAccountSchema = z.object({
  bankName: z.string().min(1, 'Bank name is required'),
  sortCode: z.string().regex(/^\d{6}$/, 'Sort code must be 6 digits'),
  accountNumber: z.string().regex(/^\d{8}$/, 'Account number must be 8 digits'),
});

type BankAccountFormData = z.infer<typeof bankAccountSchema>;

interface BankAccountFormProps {
  initialData?: BankAccount;
  onSubmit: (data: BankAccount) => void;
  isLoading?: boolean;
}

export const BankAccountForm: React.FC<BankAccountFormProps> = ({ 
  initialData, 
  onSubmit, 
  isLoading = false 
}) => {
  const form = useForm<BankAccountFormData>({
    resolver: zodResolver(bankAccountSchema),
    defaultValues: {
      bankName: initialData?.bankName || '',
      sortCode: initialData?.sortCode || '',
      accountNumber: initialData?.accountNumber || '',
    },
  });

  const handleSubmit = (data: BankAccountFormData) => {
    onSubmit({
      ...initialData,
      bankName: data.bankName,
      sortCode: data.sortCode,
      accountNumber: data.accountNumber,
    });
  };

  const formatSortCode = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Take only first 6 digits
    const truncated = digits.slice(0, 6);
    // Format as XX-XX-XX
    if (truncated.length >= 5) {
      return `${truncated.slice(0, 2)}-${truncated.slice(2, 4)}-${truncated.slice(4, 6)}`;
    } else if (truncated.length >= 3) {
      return `${truncated.slice(0, 2)}-${truncated.slice(2, 4)}`;
    } else if (truncated.length >= 1) {
      return truncated;
    }
    return '';
  };

  const handleSortCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatSortCode(e.target.value);
    const digitsOnly = formatted.replace(/\D/g, '');
    
    // Update the form field with digits only for validation
    form.setValue('sortCode', digitsOnly);
    // Update the input display with formatted value
    e.target.value = formatted;
  };

  const formatAccountNumber = (value: string) => {
    // Remove all non-digits and limit to 8 digits
    return value.replace(/\D/g, '').slice(0, 8);
  };

  return (
    <TooltipProvider>
      <div className="space-y-4">
        <div className="flex items-start space-x-2 p-3 bg-blue-50 rounded-lg">
          <InfoIcon className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-gray-700">
            <p className="mb-1">
              <strong>UK Pension Law:</strong> Required to offer pension scheme to employees earning above NI threshold.
            </p>
            <p>
              Bank details needed for pension contributions and payments.
            </p>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="bankName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bank Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g. Barclays, HSBC, Lloyds"
                      data-testid="bank-name-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="sortCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center space-x-2">
                    <span>Sort Code</span>
                    <Tooltip>
                      <TooltipTrigger>
                        <InfoIcon className="h-4 w-4 text-gray-500" />
                      </TooltipTrigger>
                      <TooltipContent side="left" align="center">
                        <p>6-digit sort code in format XX-XX-XX</p>
                      </TooltipContent>
                    </Tooltip>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="12-34-56"
                      onChange={handleSortCodeChange}
                      maxLength={8} // XX-XX-XX format
                      data-testid="sort-code-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="accountNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center space-x-2">
                    <span>Account Number</span>
                    <Tooltip>
                      <TooltipTrigger>
                        <InfoIcon className="h-4 w-4 text-gray-500" />
                      </TooltipTrigger>
                      <TooltipContent side="left" align="center">
                        <p>8-digit account number</p>
                      </TooltipContent>
                    </Tooltip>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="12345678"
                      onChange={(e) => {
                        const formatted = formatAccountNumber(e.target.value);
                        field.onChange(formatted);
                        e.target.value = formatted;
                      }}
                      maxLength={8}
                      data-testid="account-number-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading}
              data-testid="save-bank-account-button"
            >
              {isLoading ? 'Saving...' : 'Save Bank Account Details'}
            </Button>
          </form>
        </Form>
      </div>
    </TooltipProvider>
  );
};